import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Settings,
  Save,
  RefreshCw,
  Mail,
  Bell,
  Globe,
  AlertCircle,
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { adminApi, PlatformSettings } from '../../services/api/admin'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { useConfirm } from '../../contexts/ConfirmProvider'

const DEFAULT_SETTINGS: PlatformSettings = {
  deadlineReminder45DayDays: 5,
  deadlineReminder180DayDays: 14,
  vectrWorkflowIdZillowEnrichment: null,
  vectrWorkflowIdKycOrchestration: null,
  vectrWorkflowIdEsignOrchestration: null,
  vectrWorkflowIdOfacScreening: null,
  emailFromAddress: 'noreply@open1031.ai',
  maintenanceMode: false,
  demoResetEnabled: true,
}

export const AdminSettingsPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<PlatformSettings>(DEFAULT_SETTINGS)

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    loadSettings()
  }, [currentUser, navigate])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const data = await adminApi.getSettings()
      setSettings(data)
    } catch {
      toast.error('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async () => {
    try {
      setSaving(true)
      const updated = await adminApi.updateSettings(settings)
      setSettings(updated)
      toast.success('Settings saved successfully')
    } catch {
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleResetSettings = async () => {
    const ok = await confirm({
      title: 'Reset settings?',
      description: 'Unsaved changes will be lost and settings will be restored from the server.',
      icon: RefreshCw,
      iconColor: 'amber',
    })
    if (!ok) return
    loadSettings()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Settings className="h-8 w-8 text-primary" />
            Platform Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure platform-wide settings and defaults
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleResetSettings}
            variant="outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? (
              <LoadingSpinner
                size="sm"
                variant="icon"
                className="mr-2"
              />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            General
          </h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.maintenanceMode}
                onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium text-foreground">
                Maintenance Mode
              </span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.demoResetEnabled}
                onChange={(e) =>
                  setSettings({ ...settings, demoResetEnabled: e.target.checked })
                }
                className="rounded"
              />
              <span className="text-sm font-medium text-foreground">
                Demo Reset Enabled
              </span>
            </label>
          </div>
          {settings.maintenanceMode && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Maintenance mode is active. Only administrators can access the platform.
              </p>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            Email
          </h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              From Email Address
            </label>
            <Input
              type="email"
              value={settings.emailFromAddress}
              onChange={(e) => setSettings({ ...settings, emailFromAddress: e.target.value })}
              placeholder="noreply@open1031.ai"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            Deadline Reminders
          </h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              45-Day Deadline Reminder (days before)
            </label>
            <Input
              type="number"
              value={settings.deadlineReminder45DayDays}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  deadlineReminder45DayDays: Number(e.target.value),
                })
              }
              placeholder="5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              1–44 days before the 45-day identification deadline.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              180-Day Deadline Reminder (days before)
            </label>
            <Input
              type="number"
              value={settings.deadlineReminder180DayDays}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  deadlineReminder180DayDays: Number(e.target.value),
                })
              }
              placeholder="14"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              1–179 days before the 180-day exchange completion deadline.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            Vectr Workflow IDs
          </h2>
        </div>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Vectr workflow IDs used for platform automations.
          </p>
          {(
            [
              ['vectrWorkflowIdZillowEnrichment', 'Zillow Enrichment'],
              ['vectrWorkflowIdKycOrchestration', 'KYC Orchestration'],
              ['vectrWorkflowIdEsignOrchestration', 'eSign Orchestration'],
              ['vectrWorkflowIdOfacScreening', 'OFAC Screening'],
            ] as const
          ).map(([field, label]) => (
            <div key={field}>
              <label className="block text-sm font-medium text-foreground mb-1">
                {label}
              </label>
              <Input
                value={settings[field] ?? ''}
                onChange={(e) =>
                  setSettings({ ...settings, [field]: e.target.value || null })
                }
                placeholder="vectr-workflow-id"
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
